import { supabase } from '../lib/supabase';

interface ContactSubmission {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  companySize: string;
  industry: string;
  automationGoals: string[];
  currentChallenges?: string;
  monthlyTimeSpent?: string;
  preferredContactMethod?: string;
  urgency?: string;
  hearAboutUs?: string;
  additionalNotes?: string;
}

interface NewsletterSubscription {
  email: string;
  fullName?: string;
}

export const contactService = {
  // Submit contact form
  async submitContactForm(data: ContactSubmission) {
    try {
      // Insert into database
      const { data: submission, error: dbError } = await supabase
        .from('contact_submissions')
        .insert([{
          full_name: data.fullName,
          email: data.email,
          phone: data.phone,
          company: data.company,
          company_size: data.companySize,
          industry: data.industry,
          automation_goals: data.automationGoals,
          current_challenges: data.currentChallenges,
          monthly_time_spent: data.monthlyTimeSpent,
          preferred_contact_method: data.preferredContactMethod,
          urgency: data.urgency,
          hear_about_us: data.hearAboutUs,
          additional_notes: data.additionalNotes,
          contact_status: 'new'
        }])
        .select()
        .single();

      let dbInsertFailed = false;
      let dbErrorMessage: string | null = null;

      if (dbError) {
        // Don't completely fail here — allow the function to run so notifications still go out.
        dbInsertFailed = true;
        dbErrorMessage = dbError?.message ?? 'Unknown database error';
        console.error('Database insert failed (will proceed to email):', dbErrorMessage);
      }

      // Send email notification via Edge Function
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const emailResponse = await fetch(
        `${supabaseUrl}/functions/v1/send-contact-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`
          },
          body: JSON.stringify({ formData: data })
        }
      );

      const emailOk = emailResponse?.ok ?? false;
      if (!emailOk) {
        console.error('Email notification failed');
      }

      // If both operations failed, raise an error so caller can show a failure
      if (dbInsertFailed && !emailOk) {
        // Prefer returning the db error message if available
        throw new Error(dbErrorMessage ?? 'Failed to save submission and to send notification');
      }

      // Return details so the UI can show a proper message when only one of the two worked
      return {
        success: true,
        data: submission ?? null,
        dbSaved: !dbInsertFailed,
        emailSent: emailOk,
        dbError: dbErrorMessage
      };
    } catch (error: any) {
      console.error('Contact form submission error:', error);
      throw new Error(error.message || 'Failed to submit contact form');
    }
  },

  // Subscribe to newsletter
  async subscribeToNewsletter(data: NewsletterSubscription) {
    try {
      // Check if already subscribed
      const { data: existing } = await supabase
        .from('newsletter_subscriptions')
        .select('email, subscription_status')
        .eq('email', data.email)
        .single();

      if (existing) {
        if (existing.subscription_status === 'active') {
          throw new Error('This email is already subscribed to our newsletter');
        } else {
          // Reactivate subscription
          const { error: updateError } = await supabase
            .from('newsletter_subscriptions')
            .update({ 
              subscription_status: 'active',
              subscribed_at: new Date().toISOString(),
              unsubscribed_at: null
            })
            .eq('email', data.email);

          if (updateError) {
            throw new Error(`Update error: ${updateError.message}`);
          }
        }
      } else {
        // New subscription
        const { error: insertError } = await supabase
          .from('newsletter_subscriptions')
          .insert([{
            email: data.email,
            full_name: data.fullName,
            subscription_status: 'active'
          }]);

        if (insertError) {
          throw new Error(`Database error: ${insertError.message}`);
        }
      }

      // Send welcome email via Edge Function
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const emailResponse = await fetch(
        `${supabaseUrl}/functions/v1/send-newsletter-welcome`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`
          },
          body: JSON.stringify({ 
            email: data.email, 
            fullName: data.fullName 
          })
        }
      );

      if (!emailResponse.ok) {
        console.error('Welcome email failed, but subscription was saved');
      }

      return { success: true, message: 'Successfully subscribed to newsletter' };
    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      throw new Error(error.message || 'Failed to subscribe to newsletter');
    }
  },

  // Get all contact submissions (admin only - would need authentication)
  async getContactSubmissions() {
    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

      return { success: true, data };
    } catch (error: any) {
      console.error('Get contact submissions error:', error);
      throw new Error(error.message || 'Failed to fetch contact submissions');
    }
  }
};